import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import splashIcon from "../assets/images/splash-icon.png";
import { CheckCircle2, Circle, Loader2, BarChart3 } from "lucide-react";
import { completeOnboardingApi, startOnboardingImportApi } from "../api/onboarding.api";
import { showError, showSuccess } from "../utils/toast";
import {
  extractVerificationException,
  persistVerificationException,
} from "../utils/verificationException";
import type { ApiError } from "../types/api";
import Button from "../components/ui/Button";
import { getTheme } from "../config/theme";

const theme = getTheme("light").colors;
const subtleBorder = "#E7EAF0";

const buildStages = (connectedCount: number) => [
  "Google connected",
  "Location connected",
  "Importing business information",
  `Importing reviews (${connectedCount > 0 ? connectedCount : 1} location${connectedCount > 1 ? "s" : ""})`,
  "Analyzing insights",
  "Preparing dashboard",
];

export default function ImportSync() {
  const navigate = useNavigate();
  const location = useLocation();

  const connectedCount = Number(location.state?.connectedCount || 1);
  const onboardingContext = location.state?.onboardingContext || JSON.parse(localStorage.getItem("onboarding_context") || "null");
  const selectedLocations = location.state?.selectedLocations || JSON.parse(localStorage.getItem("onboarding_selected_locations") || "[]");
  const isOnboardingFlow = Boolean(onboardingContext);
  const isDevBypass =
    process.env.NODE_ENV === "development" &&
    localStorage.getItem("toronq-dev-onboarding-bypass") === "true";
  const stages = useMemo(() => buildStages(connectedCount), [connectedCount]);

  const [activeStep, setActiveStep] = useState(0);
  const [isDone, setIsDone] = useState(false);

  useEffect(() => {
    if (isOnboardingFlow) {
      const runOnboardingImport = async () => {
        try {
          if (isDevBypass) {
            showSuccess("Dev bypass: import and onboarding completion simulated");
            return;
          }

          await startOnboardingImportApi({
            selectedLocations,
            businessId: location.state?.business?.id || location.state?.businessId || selectedLocations?.[0]?.id || selectedLocations?.[0]?.placeId || null,
            onboardingContext,
          });
          await completeOnboardingApi({
            onboardingContext,
          });
          showSuccess("Import started successfully");
        } catch (rawError) {
          const error = rawError as ApiError;
          const verificationException = extractVerificationException(error);
          if (verificationException) {
            persistVerificationException(verificationException, {
              stage: "import-sync",
              selectedLocationCount: selectedLocations.length,
            });
            navigate("/kyb-verification", {
              replace: true,
              state: {
                verificationException,
                source: "import-sync",
              },
            });
            return;
          }

          const message = error?.response?.data?.message || error?.message || "Unable to start onboarding import.";
          showError(message);
        }
      };

      runOnboardingImport();
    }

    const timer = window.setInterval(() => {
      setActiveStep((prev) => {
        if (prev >= stages.length - 1) {
          window.clearInterval(timer);
          setIsDone(true);
          return prev;
        }
        return prev + 1;
      });
    }, 900);

    return () => window.clearInterval(timer);
  }, [isOnboardingFlow, isDevBypass, onboardingContext, selectedLocations, stages.length, location.state?.business?.id, location.state?.businessId, navigate]);

  useEffect(() => {
    if (!isDone) return;

    const redirectTimer = window.setTimeout(() => {
      navigate("/dashboard/overview", { replace: true });
    }, 900);

    return () => window.clearTimeout(redirectTimer);
  }, [isDone, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-8" style={{ background: theme.background }}>
      <div className="w-full max-w-xl rounded-2xl border p-8" style={{ borderColor: subtleBorder, background: theme.card }}>
        <img
          src={splashIcon}
          alt="ToronQ logo"
          className="mx-auto h-16 w-auto object-contain"
        />

        <div className="mt-6 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl" style={{ background: theme.infoBg, color: theme.primary }}>
            <BarChart3 size={22} />
          </div>

          <p className="mt-4 text-sm font-semibold" style={{ color: theme.primary }}>
            Step 7 · Import & Sync
          </p>

          <h1 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: theme.text }}>
            Importing your data
          </h1>

          <p className="mt-2 text-sm" style={{ color: theme.textMuted }}>
            We are syncing your profile, reviews, and insights. This should only take a few seconds.
          </p>
        </div>

        <div className="mt-7 space-y-3 rounded-2xl border p-4" style={{ borderColor: subtleBorder, background: theme.secondary }}>
          {stages.map((label, index) => {
            const isComplete = index < activeStep || isDone;
            const isActive = index === activeStep && !isDone;

            return (
              <div key={label} className="flex items-center gap-3">
                <div>
                  {isComplete ? (
                    <CheckCircle2 size={18} color={theme.success} />
                  ) : isActive ? (
                    <Loader2 size={18} className="animate-spin" color={theme.primary} />
                  ) : (
                    <Circle size={18} color={theme.textLight} />
                  )}
                </div>

                <p className="text-sm" style={{ color: isComplete || isActive ? theme.text : theme.textLight, fontWeight: isComplete || isActive ? 600 : 400 }}>
                  {label}
                </p>
              </div>
            );
          })}
        </div>

        <Button
          variant="secondary"
          theme={theme}
          onClick={() => navigate("/dashboard/overview", { replace: true })}
          className="mt-7 w-full py-3"
        >
          Skip wait and open dashboard
        </Button>
      </div>
    </div>
  );
}
